/* ============================================================
   Revibe Cart v2 — cart-v2.js
   Custom element: <cart-items-v2>
   ============================================================ */

class CartItemsV2 extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.initStickyOffsets();
    this.initQtyStepper();
    this.initRemove();
    this.initWarranty();
    this.initCareDetails();
    this.initCashbackSheet();
    this.initCertified();
    this.initPromo();
    this.initCheckout();
    this.initChat();
    this.initShare();
    this.initHelpTooltip();
    this.initDeliveryDates();
    this.initShippingPrices();
  }

  /* ── Sticky offsets — keeps cart header below site nav ──────── */
  initStickyOffsets() {
    const apply = () => {
      const siteHeader = document.getElementById('shopify-section-header');
      const offset = siteHeader ? siteHeader.offsetHeight : 0;
      const cartHeader = this.querySelector('.cv2-header');
      const summaryCol = this.querySelector('.cv2-col-summary');
      if (cartHeader) cartHeader.style.top = offset + 'px';
      if (summaryCol) summaryCol.style.top = (offset + 16) + 'px';
    };
    apply();
    window.addEventListener('resize', apply, { passive: true });
  }

  /* ── Quantity stepper ─────────────────────────────────────── */
  // Reads the cross-sell child keys stamped by the section on the
  // parent card (data-cv2-cross-keys="key1|key2|...") so qty/remove can
  // keep them synced with the parent the same way it already does for
  // the linked warranty line (data-cv2-warranty-key).
  crossSellKeysFrom(el) {
    return (el.dataset.cv2CrossKeys || '').split('|').filter(Boolean);
  }

  initQtyStepper() {
    this.querySelectorAll('[data-cv2-minus]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.classList.contains('cv2-is-loading')) return;
        const card = btn.closest('[data-cv2-item]');
        if (!card) return;
        const qtyEl = card.querySelector('[data-cv2-qty]');
        const current = qtyEl ? (parseInt(qtyEl.textContent) || 1) : 1;
        const lineKey = card.dataset.cv2Item;
        const warrantyKey = card.dataset.cv2WarrantyKey || '';
        const crossKeys = this.crossSellKeysFrom(card);
        if (current <= 1) {
          this.removeItem(lineKey, warrantyKey, crossKeys);
        } else {
          const newQty = current - 1;
          this.changeLineQty(lineKey, newQty, warrantyKey, crossKeys);
        }
      });
    });

    this.querySelectorAll('[data-cv2-plus]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.classList.contains('cv2-is-loading')) return;
        const card = btn.closest('[data-cv2-item]');
        if (!card) return;
        const qtyEl = card.querySelector('[data-cv2-qty]');
        const current = qtyEl ? (parseInt(qtyEl.textContent) || 1) : 1;
        const lineKey = card.dataset.cv2Item;
        const warrantyKey = card.dataset.cv2WarrantyKey || '';
        const crossKeys = this.crossSellKeysFrom(card);
        const newQty = current + 1;
        this.changeLineQty(lineKey, newQty, warrantyKey, crossKeys);
      });
    });
  }

  changeLineQty(lineKey, qty, warrantyKey, crossKeys) {
    this.setLoading(true);
    const updates = { [lineKey]: qty };
    if (warrantyKey) updates[warrantyKey] = qty;
    (crossKeys || []).forEach(key => { updates[key] = qty; });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then(() => this.reloadSection())
      .catch(() => { this.setLoading(false); window.location.reload(); });
  }

  /* ── Remove item ──────────────────────────────────────────── */
  initRemove() {
    this.querySelectorAll('[data-cv2-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.classList.contains('cv2-is-loading')) return;
        const lineKey = btn.dataset.cv2Remove;
        const warrantyKey = btn.dataset.cv2WarrantyKey || '';
        const crossKeys = this.crossSellKeysFrom(btn);
        this.removeItem(lineKey, warrantyKey, crossKeys);
      });
    });
  }

  removeItem(lineKey, warrantyKey, crossKeys) {
    this.setLoading(true);
    const updates = { [lineKey]: 0 };
    if (warrantyKey) updates[warrantyKey] = 0;
    (crossKeys || []).forEach(key => { updates[key] = 0; });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then(() => this.reloadSection())
      .catch(() => { this.setLoading(false); window.location.reload(); });
  }

  /* ── Warranty add / remove ────────────────────────────────── */
  initWarranty() {
    this.querySelectorAll('[data-cv2-warranty-add]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.classList.contains('cv2-is-loading')) return;
        if (typeof addWarranty !== 'function') return;
        this.setLoading(true);

        // addWarranty() doesn't return a Promise; it signals completion by
        // calling updateStickyFooterPrice(). Hook that to fire reloadSection().
        const origFn = window.updateStickyFooterPrice;
        let active = true;

        const restore = () => {
          if (!active) return;
          active = false;
          window.updateStickyFooterPrice = origFn;
          clearTimeout(fallback);
        };

        const fallback = setTimeout(() => {
          restore();
          this.setLoading(false);
          window.location.reload();
        }, 10000);

        window.updateStickyFooterPrice = (...args) => {
          restore();
          if (origFn) origFn.apply(window, args);
          this.reloadSection();
        };

        addWarranty(btn);
      });
    });

    this.querySelectorAll('[data-cv2-warranty-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.classList.contains('cv2-is-loading')) return;
        const warrantyKey = btn.dataset.cv2WarrantyRemove;
        if (!warrantyKey) return;
        this.setLoading(true);
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: warrantyKey, quantity: 0 }),
        })
          .then(() => this.reloadSection())
          .catch(() => { this.setLoading(false); window.location.reload(); });
      });
    });
  }

  /* ── Care details toggle ──────────────────────────────────── */
  initCareDetails() {
    this.querySelectorAll('[data-cv2-care-info-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetId = btn.dataset.cv2CareInfoToggle;
        const panel = document.getElementById(targetId);
        if (!panel) return;
        const isOpen = !panel.hidden;
        panel.hidden = isOpen;
        this.querySelectorAll(`[data-cv2-care-info-toggle="${targetId}"]`).forEach(b => {
          b.setAttribute('aria-expanded', String(!isOpen));
          b.classList.toggle('cv2-care-info-btn--open', !isOpen);
        });
      });
    });
  }

  /* ── Cashback sheet ───────────────────────────────────────── */
  initCashbackSheet() {
    const sheet = document.getElementById('cv2-cashback-sheet');
    if (!sheet) return;

    const open = () => {
      sheet.hidden = false;
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      sheet.hidden = true;
      document.body.style.overflow = '';
    };

    this.querySelectorAll('[data-cv2-cashback-open]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        open();
      });
    });
    sheet.querySelectorAll('[data-cv2-cashback-close]').forEach(el => {
      el.addEventListener('click', close);
    });
    sheet.querySelector('.cv2-cashback-sheet__backdrop')?.addEventListener('click', close);
  }

  /* ── Certified collapsible ────────────────────────────────── */
  initCertified() {
    this.querySelectorAll('[data-cv2-certified-trigger]').forEach(trigger => {
      const wrapper = trigger.closest('[data-cv2-certified-wrap]');
      if (!wrapper) return;
      const body = wrapper.querySelector('[data-cv2-certified-body]');
      const chevron = wrapper.querySelector('[data-cv2-certified-chevron]');

      trigger.addEventListener('click', () => {
        const isOpen = !body.hidden;
        body.hidden = isOpen;
        chevron?.classList.toggle('cv2-certified__chevron--open', !isOpen);
      });
    });
  }

  /* ── Promo code ───────────────────────────────────────────── */
  initPromo() {
    const inputs = this.querySelectorAll('[data-cv2-promo-input]');

    // Pre-fill all inputs from localStorage and keep them in sync
    const stored = localStorage.getItem('storedDiscount');
    inputs.forEach(input => {
      if (stored) input.value = stored;
      input.addEventListener('input', () => {
        const val = input.value.trim();
        localStorage.setItem('storedDiscount', val);
        inputs.forEach(other => { if (other !== input) other.value = val; });
      });
    });

    // Apply
    this.querySelectorAll('[data-cv2-promo-apply]').forEach(applyBtn => {
      applyBtn.addEventListener('click', () => {
        const activeInput = this.querySelector('[data-cv2-promo-input]');
        const code = activeInput ? activeInput.value.trim() : '';
        if (!code) return;

        this.querySelectorAll('[data-cv2-promo-apply]').forEach(b => { b.classList.add('cv2-apply-btn--loading'); b.disabled = true; });

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: code })
        })
          .then(r => r.json())
          .then(cart => {
            const match = Array.isArray(cart.discount_codes) &&
              cart.discount_codes.find(d => d.code.toUpperCase() === code.toUpperCase());

            if (match && match.applicable === true) {
              localStorage.setItem('storedDiscount', code);
              this.querySelectorAll('[data-cv2-promo-input]').forEach(el => { el.value = ''; });
              this.showPromoFeedback(true);
              return this.reloadSection();
            } else if (match) {
              this.showPromoFeedback(false, 'not_applicable');
            } else {
              this.showPromoFeedback(false, 'invalid');
            }
          })
          .catch(() => { this.setLoading(false); })
          .finally(() => {
            this.querySelectorAll('[data-cv2-promo-apply]').forEach(b => { b.classList.remove('cv2-apply-btn--loading'); b.disabled = false; });
          });
      });
    });

    // Remove
    this.querySelectorAll('[data-cv2-promo-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.classList.contains('cv2-is-loading')) return;
        localStorage.removeItem('storedDiscount');
        inputs.forEach(input => { input.value = ''; });
        this.setLoading(true);
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '' })
        })
          .then(() => this.reloadSection())
          .catch(() => { this.setLoading(false); });
      });
    });
  }

  showPromoFeedback(success, reason) {
    const isRTL = document.documentElement.dir === 'rtl';
    let msg;
    if (success) {
      msg = isRTL ? 'تم تطبيق رمز الخصم بنجاح!' : 'Code applied successfully!';
    } else if (reason === 'already_applied') {
      msg = isRTL ? 'هذا الكود مطبق بالفعل' : 'Code already applied';
    } else if (reason === 'not_applicable') {
      msg = isRTL ? 'الكود غير قابل للتطبيق على منتجات سلتك' : "Code isn't applicable to items in your cart";
    } else {
      msg = isRTL ? 'كود غير صالح أو منتهي الصلاحية' : 'Invalid or expired code';
    }
    this.querySelectorAll('[data-cv2-promo-hint]').forEach(hint => {
      hint.textContent = msg;
      hint.classList.toggle('cv2-promo-hint--error', !success);
      hint.classList.add('cv2-promo-hint--visible');
    });
    clearTimeout(this._promoHintTimer);
    this._promoHintTimer = setTimeout(() => {
      this.querySelectorAll('[data-cv2-promo-hint]').forEach(hint => {
        hint.classList.remove('cv2-promo-hint--visible');
      });
    }, 5000);
  }

  /* ── Checkout ─────────────────────────────────────────────── */
  initCheckout() {
    this.querySelectorAll('[data-cv2-checkout]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (btn.disabled) return;
        btn.disabled = true;

        const coupon = this.querySelector('[data-cv2-promo-input]')?.value?.trim();
        if (coupon) {
          localStorage.setItem('storedDiscount', coupon);
          try { await fetch(`/discount/${encodeURIComponent(coupon)}`); } catch (_) {}
        }

        const href = btn.dataset.cv2Checkout;

        if (typeof checkoutBtnEventHandler === 'function') {
          checkoutBtnEventHandler(e);
        } else {
          window.location.href = href;
        }
      });
    });
  }

  /* ── Help tooltip ─────────────────────────────────────────── */
  initHelpTooltip() {
    const wrap = this.querySelector('.cv2-tooltip-wrap');
    const btn = wrap?.querySelector('.cv2-header__action-btn--help');
    if (!wrap || !btn) return;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = wrap.classList.toggle('cv2-tooltip--open');
      btn.setAttribute('aria-expanded', isOpen);
    });

    document.addEventListener('click', () => {
      wrap.classList.remove('cv2-tooltip--open');
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  /* ── Share cart via email ─────────────────────────────────── */
  initShare() {
    const btn = this.querySelector('.cv2-header__action-btn--share');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const path = btn.dataset.cv2Share;
      const url = `${window.location.origin}${path}`;
      const text = encodeURIComponent(`Here's my Revibe cart:\n${url}`);
      window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
    });
  }

  /* ── Chat button ──────────────────────────────────────────── */
  initChat() {
    const btn = this.querySelector('.cv2-header__action-btn--chat');
    if (!btn) return;
    btn.addEventListener('click', function() {
      if (typeof loadAlhenaChat === 'function') {
        loadAlhenaChat({ openChat: true, buttonEl: this });
      }
    });
  }

  /* ── Delivery dates ───────────────────────────────────────── */
  initDeliveryDates() {
    const store = this.dataset.store || '';
    const isRTL = document.documentElement.dir === 'rtl';

    this.querySelectorAll('[data-cv2-delivery]').forEach(tag => {
      const textEl = tag.querySelector('[data-cv2-delivery-text]');
      if (!textEl) return;
      const isExpress = tag.dataset.cv2Express === 'true';

      const range = this.getDeliveryRules(store, isExpress);
      if (!range) return;

      const dateStr = this.formatDeliveryDate(range[0], range[1]);
      const prefix = isExpress
        ? (isRTL ? 'توصيل سريع مجاني' : 'Free express delivery')
        : (isRTL ? 'توصيل مجاني' : 'Free delivery');

      textEl.textContent = `${prefix} · ${dateStr}`;

      if (isExpress) {
        tag.className = tag.className.replace('cv2-delivery-tag--standard', 'cv2-delivery-tag--express');
      }
    });

    const globalDateEl = this.querySelector('[data-cv2-global-delivery]');
    if (globalDateEl) {
      const range = this.getDeliveryRules(store, false);
      if (range) globalDateEl.textContent = this.formatDeliveryDate(range[0], range[1]);
    }
  }

  getDeliveryRules(region, fbr = false) {
    const region_rules = {
      uae: {
        "1": [1, 3], "2": [1, 3], "3": [1, 5], "4": [1, 5], "5": [3, 5], "6": [2, 4], "7": [1, 3],
        revibe_fulfilled: { "1": [1, 2], "2": [1, 2], "3": [1, 2], "4": [1, 4], "5": [1, 3], "6": [2, 3], "7": [1, 2] }
      },
      za: {
        "1": [3, 7], "2": [3, 6], "3": [5, 6], "4": [5, 6], "5": [5, 6], "6": [5, 6], "7": [4, 5],
        revibe_fulfilled: { "1": [2, 3], "2": [2, 3], "3": [2, 4], "4": [4, 5], "5": [4, 5], "6": [3, 4], "7": [2, 3] }
      },
      ksa: {
        "1": [3, 7], "2": [3, 6], "3": [5, 6], "4": [5, 6], "5": [5, 6], "6": [5, 6], "7": [4, 5]
      },
      ph: {
        "1": [7, 7], "2": [7, 7], "3": [7, 7], "4": [7, 7], "5": [7, 7], "6": [7, 7], "7": [7, 7]
      },
      ng: {
        "1": [7, 7], "2": [7, 7], "3": [7, 7], "4": [7, 7], "5": [7, 7], "6": [7, 7], "7": [7, 7]
      },
      ke: {
        "1": [7, 7], "2": [7, 7], "3": [7, 7], "4": [7, 7], "5": [7, 7], "6": [7, 7], "7": [7, 7]
      },
      other_regions: {
        "1": [2, 4], "2": [2, 5], "3": [2, 5], "4": [4, 6], "5": [4, 6], "6": [3, 5], "7": [2, 4]
      }
    };

    const day = String((() => {
      const d = new Date().getDay();
      return d === 0 ? 7 : d;
    })());

    const rules = region_rules[region] || region_rules.other_regions;
    const table = (fbr && rules.revibe_fulfilled) ? rules.revibe_fulfilled : rules;

    // PH/NG/KE: Saturday orders after 2pm store-local time ship as if placed Sunday
    const cutoffTimeZone = { ph: "Asia/Manila", ng: "Africa/Lagos", ke: "Africa/Nairobi" }[region];
    if (cutoffTimeZone) {
      const localParts = new Intl.DateTimeFormat("en-US", {
        timeZone: cutoffTimeZone,
        weekday: "short",
        hour: "numeric",
        hourCycle: "h23"
      }).formatToParts(new Date());
      const localWeekday = localParts.find(p => p.type === "weekday").value;
      const localHour = Number(localParts.find(p => p.type === "hour").value);
      if (localWeekday === "Sat" && localHour >= 14) {
        return [8, 8];
      }
    }

    return table[day] || null;
  }

  formatDeliveryDate(minDays, maxDays) {
    const MONTHS_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
    const isRTL = document.documentElement.dir === 'rtl';
    const months = isRTL ? MONTHS_AR : MONTHS_EN;

    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() + minDays);
    const end   = new Date(today); end.setDate(today.getDate() + maxDays);

    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    // start and end land on the same day (e.g. PH) — show a single date, no range
    const sameDay = start.getTime() === end.getTime();

    return sameDay
      ? `${start.getDate()} ${months[start.getMonth()]}`
      : sameMonth
        ? `${start.getDate()} - ${end.getDate()} ${months[end.getMonth()]}`
        : `${start.getDate()} ${months[start.getMonth()]} - ${end.getDate()} ${months[end.getMonth()]}`;
  }

  /* ── Section AJAX reload ──────────────────────────────────── */
  reloadSection() {
    const urlPrefix = window.location.pathname;
    fetch(`${urlPrefix}?section_id=${this.sectionId}`, { cache: 'no-cache' })
      .then(r => r.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
        const newEl = doc.querySelector('cart-items-v2');
        if (newEl) {
          this.innerHTML = newEl.innerHTML;
          // Sync own attributes so initShippingPrices reads the updated cart total
          if (newEl.dataset.cartTotal !== undefined) this.dataset.cartTotal = newEl.dataset.cartTotal;
          this.connectedCallback();
          // Sync global cart count badge
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              document.querySelectorAll('[data-cart-count]').forEach(el => {
                console.log("updating cart item count ", cart.item_count)
                el.textContent = cart.item_count;
              });
            })
            .catch(() => {});
        } else {
          window.location.reload();
        }
      })
      .catch(() => window.location.reload())
      .finally(() => this.setLoading(false));
  }

  /* ── Shipping price cache + display ──────────────────────── */
  initShippingPrices() {
    const CACHE_KEY = 'shippingPriceDetails';
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          this.updateShippingDisplay(parsed);
          return;
        }
      }
    } catch (e) {}

    fetch('https://api.revibe.me/shopify/public/fetchDeliveryPrices.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shopUrl: 'revibe-me.myshopify.com' })
    })
      .then(r => r.json())
      .then(data => {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        this.updateShippingDisplay(data);
      })
      .catch(() => {});
  }

  updateShippingDisplay(data) {
    if (!this.querySelector('[data-cv2-promo-remove]')) return;

    const methods = data?.shippingMethods;
    if (!Array.isArray(methods)) return;

    // Determine if every cart item is express
    const deliveryTags = Array.from(this.querySelectorAll('[data-cv2-delivery]'));
    const allExpress = deliveryTags.length > 0 && deliveryTags.every(t => t.dataset.cv2Express === 'true');

    const method = allExpress
      ? (methods.find(m => m.methodName === 'Revibe Express') || methods.find(m => m.methodName === 'Standard'))
      : methods.find(m => m.methodName === 'Standard');
    if (!method) return;

    const shippingCents  = Math.round(parseFloat(method.price) * 100);
    const cartTotalCents = parseInt(this.dataset.cartTotal || '0', 10);
    const newTotalCents  = cartTotalCents + shippingCents;

    const fmt = cents => {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney && window.money_format) {
        return Shopify.formatMoney(cents, window.money_format);
      }
      return method.currency + ' ' + (cents / 100).toFixed(2);
    };

    // Shipping line value
    this.querySelectorAll('[data-cv2-shipping-value]').forEach(el => {
      el.textContent = fmt(shippingCents);
      el.classList.remove('cv2-summary-line__value--free');
    });

    // Order total rows
    this.querySelectorAll('.cv2-summary-total__amount').forEach(el => {
      el.textContent = fmt(newTotalCents);
    });

    // Checkout button price label  (format: "Checkout · AED X")
    this.querySelectorAll('.cv2-checkout-btn__content').forEach(el => {
      const label = (el.textContent.split('·')[0] || '').trimEnd();
      el.textContent = label + ' · ' + fmt(newTotalCents);
    });

    // Cashback visibility — only show when effective total > 1000 AED (100 000 cents)
    const showCashback = newTotalCents > 100000;
    this.querySelectorAll('.cv2-checkout-btn__cashback-badge, .cv2-cashback-card').forEach(el => {
      el.hidden = !showCashback;
    });
    this.querySelectorAll('.cv2-checkout-btn').forEach(btn => {
      btn.classList.toggle('cv2-checkout-btn--cashback-enabled', showCashback);
      btn.classList.toggle('cv2-checkout-btn--cashback-disabled', !showCashback);
    });

    // Delivery text: strip "Free" prefix when shipping is charged
    const isRTL = document.documentElement.dir === 'rtl';
    this.querySelectorAll('[data-cv2-delivery]').forEach(tag => {
      const textEl = tag.querySelector('[data-cv2-delivery-text]');
      if (!textEl) return;
      const isExpress = tag.dataset.cv2Express === 'true';
      const datePart = textEl.textContent.includes('·')
        ? textEl.textContent.split('·').slice(1).join('·').trim()
        : '';
      const newPrefix = isExpress
        ? (isRTL ? 'توصيل سريع' : 'Express delivery')
        : (isRTL ? 'توصيل قياسي' : 'Standard delivery');
      textEl.textContent = datePart ? `${newPrefix} · ${datePart}` : newPrefix;
    });
  }

  setLoading(on) {
    this.classList.toggle('cv2-is-loading', on);
  }

  disconnectedCallback() {
    document.body.style.overflow = '';
  }
}

customElements.define('cart-items-v2', CartItemsV2);